import React, { Component } from 'react';
import Wahanda from 'common/wahanda';
import classnames from 'classnames';
import { DisabledWithTooltip } from 'components/common/DisabledWithTooltip';
import { Radio } from 'components/common/Radio';
import { Button } from 'components/common/Button';
import Close from 'components/common/icons/Close';
import Tick from 'components/common/icons/Tick';
import { GREEN } from 'components/common/icons/Tick/types';
import { DisableReason, DISABLE_REASON } from 'components/employees/employeeEditPolicy';
import EmployeeCustomPermissionsDialog from './EmployeeCustomPermissionsDialog';
import style from './style.scss';

const lang = Wahanda.lang.settings.employees.employee;
interface IEmployeeRolesListItemProps extends React.HTMLAttributes<Element> {
  role: any;
  selected?: boolean;
  permissions?: string[];
  label: string;
  descriptions: {
    hideIcon?: boolean;
    disabled?: boolean;
    label?: string;
  }[];
  disabled?: boolean;
  disableReason?: DisableReason;
  presetDisabled?: boolean;
  presetDisableReason?: DisableReason;
  actorPermissions?: string[];
  pointOfSalePermissionsVisible?: boolean;
  autoOpenCustomPermissionsDialog?: boolean;
  onRoleSelect?: (...args: any[]) => any;
  onPermissionsSelect?: (...args: any[]) => any;
}
type EmployeeRolesListItemState = {
  customPermissionsDialogVisible?: boolean;
};
class EmployeeRolesListItem extends Component<
  IEmployeeRolesListItemProps,
  EmployeeRolesListItemState
> {
  static defaultProps = {
    permissions: [],
    selected: false,
    disabled: false,
    actorPermissions: [],
    pointOfSalePermissionsVisible: false,
    autoOpenCustomPermissionsDialog: false,
    presetDisabled: false,
    onRoleSelect: () => {},
    onPermissionsSelect: () => {},
  };

  state = {
    customPermissionsDialogVisible: false,
  };

  getInteractionState = () => {
    const { disabled, disableReason, presetDisabled, presetDisableReason, selected } = this.props;

    if (disabled) {
      return {
        roleSelectionDisabled: true,
        customiseDisabled: true,
        roleDisableReason: disableReason,
      };
    }

    if (presetDisabled) {
      return {
        roleSelectionDisabled: true,
        customiseDisabled: !selected,
        roleDisableReason: presetDisableReason ?? DISABLE_REASON.CANNOT_GRANT_FULL_PRESET_ROLE,
      };
    }

    return {
      roleSelectionDisabled: false,
      customiseDisabled: false,
      roleDisableReason: undefined,
    };
  };

  onRoleChange = () => {
    const { role, autoOpenCustomPermissionsDialog } = this.props;
    const { roleSelectionDisabled } = this.getInteractionState();

    if (roleSelectionDisabled) {
      return;
    }
    // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    this.props.onRoleSelect(role);
    if (autoOpenCustomPermissionsDialog) {
      this.setState({ customPermissionsDialogVisible: true });
    }
  };

  onCustomiseButtonClick = () => {
    const { customiseDisabled } = this.getInteractionState();

    if (customiseDisabled) {
      return;
    }
    this.setState({ customPermissionsDialogVisible: true });
  };

  onCustomPermissionsDialogClose = () => {
    this.setState({ customPermissionsDialogVisible: false });
  };

  onCustomPermissionsDialogSave = (permissions) => {
    this.setState({ customPermissionsDialogVisible: false });
    // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    this.props.onPermissionsSelect(permissions);
  };

  renderDescriptions = () => {
    const { descriptions } = this.props;
    return (
      <div>
        {descriptions.map((description) => (
          <span key={description.label} className={style.description}>
            {!description.hideIcon && (
              <span className={style.iconContainer}>
                {description.disabled ? (
                  <Close className={style.closeIcon} />
                ) : (
                  <Tick colour={GREEN} small />
                )}
              </span>
            )}
            {description.label}
          </span>
        ))}
      </div>
    );
  };

  wrapRoleWithTooltip = (content: React.ReactNode) => {
    const { roleSelectionDisabled, roleDisableReason } = this.getInteractionState();

    if (!roleSelectionDisabled || !roleDisableReason) {
      return content;
    }

    return <DisabledWithTooltip reason={roleDisableReason}>{content}</DisabledWithTooltip>;
  };

  render() {
    const {
      role,
      selected,
      label,
      permissions,
      disabled,
      disableReason,
      actorPermissions,
      pointOfSalePermissionsVisible,
    } = this.props;
    const { roleSelectionDisabled, customiseDisabled } = this.getInteractionState();
    const classes = classnames({
      [style.listItem]: true,
      [style.active]: selected,
      [style.disabled]: disabled,
      [style.roleSelectionDisabled]: roleSelectionDisabled && !disabled,
    });

    const roleContent = (
      <div className={style.role} onClick={this.onRoleChange}>
        {/* @ts-expect-error ts-migrate(2741) FIXME: Property 'label' is missing in type '{ value: any;... Remove this comment to see the full error message */}
        <Radio
          value={role}
          name="employee-role"
          onChange={this.onRoleChange}
          checked={selected}
          disabled={roleSelectionDisabled}
        />
        <div className={style.labelContainer}>
          {label}
          {this.renderDescriptions()}
        </div>
      </div>
    );

    const customiseButton = (
      <Button
        label={lang.buttons.viewAndCustomise}
        onClick={this.onCustomiseButtonClick}
        variant="secondary"
        size="small"
        disabled={customiseDisabled}
      />
    );

    return (
      <div className={classes}>
        {this.wrapRoleWithTooltip(roleContent)}
        <div className={style.customiseButtonContainer}>{customiseButton}</div>
        {this.state.customPermissionsDialogVisible && (
          <EmployeeCustomPermissionsDialog
            onClose={this.onCustomPermissionsDialogClose}
            onSave={this.onCustomPermissionsDialogSave}
            permissions={permissions}
            actorPermissions={actorPermissions}
            pointOfSalePermissionsVisible={pointOfSalePermissionsVisible}
            readOnly={disabled}
            readOnlyReason={disableReason}
          />
        )}
      </div>
    );
  }
}

export default EmployeeRolesListItem;
